/* Project allocation grid + Person timeline.
   Verbatim TSX port of designv2/src/views_grid.jsx (ProjectDetailView +
   PersonDetailView). MonthHeader is local; windowMonths /
   useScrollCurrentIntoView / prevMonthLabel live in ../lib/grid-helpers. */
import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { hoursToPercent, whForMonth } from "@shared/allocation";
import type {
  ActualsByProject,
  AllocationCell,
  Member,
  Person,
  Project,
  YmKey,
} from "@shared/types";

import { AllocCell, AssignPicker, MultiSelect, RolePicker } from "../cells";
import {
  JOB_ROLES_SEED,
  aggregatePersonAllocations,
  allocToHours,
  downloadCsv,
  getActualHours,
  getPerson,
  getProjectRates,
  memberRole,
  roleTone,
  sortMembers,
} from "../data";
import { parseAllocInput } from "../lib/alloc-input";
import { apiFetch } from "../lib/api";
import { prevMonthLabel, useScrollCurrentIntoView, windowMonths } from "../lib/grid-helpers";
import { extrasForYmKey } from "../lib/holiday-cache";
import type { SpaMode } from "../lib/me-api";
import {
  addPersonMembership,
  addProjectMember,
  deleteActualCell,
  deleteAllocationCell,
  deleteProjectMember,
  patchProjectMember,
  putActualCell,
  putAllocationCell,
} from "../lib/memberships-api";
import { sortByName } from "../lib/sort";
import { usePersistedState } from "../lib/viewState";
import { useC } from "../theme";
import { CURRENT_MONTH_KEY, type MonthEntry, PREV_MONTH_KEY } from "../tokens";
import { Av, Badge, Btn, Card, Empty, Modal, SBadge, Seg, useTableStyles } from "../ui";

function MonthHeader({ m, isCurrent }: { m: MonthEntry; isCurrent: boolean }) {
  const { C } = useC();
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: isCurrent ? 700 : 600,
        color: isCurrent ? C.blue : m.isPast ? C.slateMut : C.navy,
      }}
    >
      {m.label}
    </span>
  );
}

const ActualCell: FC<{
  hours: number;
  pct: number;
  viewMode: "pct" | "hours";
  capacityHours: number;
  isCurrent: boolean;
  disabled?: boolean;
  onChange: (hours: number) => void;
  testId?: string;
}> = ({ hours, pct, viewMode, capacityHours, isCurrent, disabled = false, onChange, testId }) => {
  const { C } = useC();
  const { TD } = useTableStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const committedRef = useRef(false);
  const focusInput = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.focus();
      node.select();
    }
  }, []);
  const beginEdit = () => {
    committedRef.current = false;
    setDraft(hours > 0 ? String(viewMode === "hours" ? Math.round(hours * 100) / 100 : pct) : "");
    setEditing(true);
  };
  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const parsed = parseAllocInput(draft);
    if (parsed === null) {
      onChange(0);
    } else if (viewMode === "hours") {
      onChange(parsed);
    } else {
      onChange((parsed / 100) * capacityHours);
    }
    setEditing(false);
  };
  if (editing) {
    return (
      <td
        {...(testId ? { "data-testid": testId } : {})}
        style={{ ...TD, textAlign: "center", background: C.blueTint2, padding: "3px 2px" }}
      >
        <input
          ref={focusInput}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Escape") {
              committedRef.current = true;
              setEditing(false);
            }
          }}
          style={{
            width: 46,
            padding: "3px 2px",
            borderRadius: 3,
            border: `2px solid ${C.blue}`,
            fontSize: 13,
            fontWeight: 600,
            color: C.blue,
            textAlign: "center",
            outline: "none",
            background: C.surface,
            boxSizing: "border-box",
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          }}
        />
        <span style={{ fontSize: 10, color: C.slateMut }}>{viewMode === "hours" ? "h" : "%"}</span>
      </td>
    );
  }
  const label = hours > 0 ? (viewMode === "hours" ? `${Math.round(hours)}h` : `${pct}%`) : "·";
  const bg = hours > 0 || isCurrent ? C.blueTint1 : "transparent";
  const co = hours > 0 ? C.navy : C.slateDim;
  return (
    <td
      {...(testId ? { "data-testid": testId } : {})}
      style={{
        ...TD,
        textAlign: "center",
        background: bg,
        minWidth: 58,
        fontVariantNumeric: "tabular-nums",
        userSelect: "none",
        padding: 0,
      }}
      title={viewMode === "hours" ? "Click to edit logged hours" : "Click to edit actual %"}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={beginEdit}
        style={{
          width: "100%",
          minHeight: 26,
          border: "none",
          background: "transparent",
          cursor: disabled ? "wait" : "pointer",
          font: "inherit",
          padding: "4px 6px",
        }}
      >
        <span style={{ fontWeight: hours > 0 ? 600 : 400, color: co, fontSize: 12.5 }}>
          {label}
        </span>
      </button>
    </td>
  );
};

export interface ProjectDetailViewProps {
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  people: Person[];
  projects: Project[];
  setProjects: (next: Project[]) => void;
  rangeStart: string;
  rangeEnd: string;
  mode: SpaMode;
  actuals: ActualsByProject;
  setActuals: React.Dispatch<React.SetStateAction<ActualsByProject>>;
  role: string;
  onEdit?: (project: Project) => void;
  openPerson?: (pid: number) => void;
}

export const ProjectDetailView: FC<ProjectDetailViewProps> = ({
  selectedIds,
  setSelectedIds,
  people,
  projects,
  setProjects,
  rangeStart,
  rangeEnd,
  mode,
  actuals,
  setActuals,
  role,
  onEdit,
  openPerson,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const months = windowMonths(rangeStart, rangeEnd);
  const selProjects = projects.filter((p) => selectedIds.includes(p.id));
  const canSyncJira = role === "admin";
  const [syncBusyForProject, setSyncBusyForProject] = useState<number | null>(null);
  const [syncToast, setSyncToast] = useState<string | null>(null);
  const [syncConfirm, setSyncConfirm] = useState<Project | null>(null);
  const [syncConfirmCode, setSyncConfirmCode] = useState("");
  const [pendingActualCells, setPendingActualCells] = useState<Set<string>>(() => new Set());

  async function runJiraSync(
    project: Project,
    scope: "recent" | "all",
    confirmProjectCode?: string,
  ) {
    setSyncBusyForProject(project.id);
    setSyncToast(null);
    try {
      const res = await apiFetch(`/api/projects/${project.id}/actuals/sync-from-jira`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(scope === "all" ? { scope, confirmProjectCode } : { scope }),
      });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => ({}));
        const message =
          typeof body === "object" && body !== null && "message" in body
            ? typeof body.message === "string"
              ? body.message
              : null
            : null;
        throw new Error(message ?? `sync failed (${res.status})`);
      }
      const body = (await res.json()) as {
        cells_upserted: number;
        worklogs_seen: number;
        skipped_total: number;
        overlap_warnings?: number;
      };
      const overlap = body.overlap_warnings
        ? ` (${body.overlap_warnings} issues overlap with other RP projects)`
        : "";
      setSyncToast(
        `Synced ${project.code}: ${body.cells_upserted} cells written, ${body.worklogs_seen} worklogs, ${body.skipped_total} skipped${overlap}.`,
      );
      // Refetch this project's actuals.
      try {
        const r = await apiFetch(`/api/projects/${project.id}/actuals`);
        if (r.ok) {
          const j = (await r.json()) as Record<string, Record<string, number>>;
          setActuals((prev) => ({ ...prev, [project.id]: j }));
        }
      } catch {
        /* ignore — manual refresh recovers */
      }
    } catch (err) {
      setSyncToast(`Sync failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSyncBusyForProject(null);
    }
  }

  // Fetch per-project actuals for every selected id and merge into App-level
  // state. AbortController ensures a stale fetch (selectedIds changed
  // mid-flight) does not overwrite a fresher response — the in-flight
  // request is cancelled before the new effect fires.
  useEffect(() => {
    const ctrl = new AbortController();
    async function load() {
      const results = await Promise.all(
        selectedIds.map(async (id) => {
          try {
            const res = await apiFetch(`/api/projects/${id}/actuals`, { signal: ctrl.signal });
            if (!res.ok) return null;
            const body = (await res.json()) as Record<string, Record<string, number>>;
            return { id, body };
          } catch {
            return null;
          }
        }),
      );
      if (ctrl.signal.aborted) return;
      setActuals((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (!r) continue;
          next[String(r.id)] = r.body;
        }
        return next;
      });
    }
    void load();
    return () => {
      ctrl.abort();
    };
  }, [selectedIds, setActuals]);
  useScrollCurrentIntoView(rangeStart, rangeEnd, `proj:${selectedIds.join(",")}`);
  const [viewMode, setViewMode] = usePersistedState<"pct" | "hours">(
    "projects",
    "displayUnit",
    "pct",
  );
  const isActualMode = mode === "actuals";
  const zoom = 1;
  /* Edit boundary: admin can rewrite history; everyone else is locked out of
     months earlier than PREV_MONTH_KEY (so the just-closed month stays open
     for reforecasts).  `lockPast` is still used for the subtitle copy. */
  const editBeforeKey = role === "admin" || role === "lead" ? null : PREV_MONTH_KEY;
  const lockPast = role !== "admin" && role !== "lead";

  const cellMin = Math.round(58 * zoom);

  function cellInputToBarePercent(
    val: AllocationCell,
    person: Pick<Person, "wl"> | null | undefined,
    ymKey: YmKey,
  ): number {
    if (val.u === "percent") return val.v;
    return hoursToPercent(val.v, person?.wl ?? 100, whForMonth(ymKey, extrasForYmKey(ymKey)));
  }

  async function refetchProject(projId: number): Promise<void> {
    try {
      const res = await apiFetch(`/api/projects/${projId}`);
      if (!res.ok) return;
      const refreshed = (await res.json()) as Project;
      setProjects(projects.map((p: Project) => (p.id === projId ? refreshed : p)));
    } catch {
      // best-effort — apiFetch already handles single-flight token refresh
    }
  }

  async function updateActual(projId: number, personId: number, mk: YmKey, hours: number) {
    const cellKey = `${projId}:${personId}:${mk}`;
    if (pendingActualCells.has(cellKey)) return;
    setPendingActualCells((prev) => new Set(prev).add(cellKey));
    const rounded = Math.max(0, Math.round(hours * 100) / 100);
    const projectKey = String(projId);
    const personKey = String(personId);
    setActuals((prev) => {
      const nextPersonActuals = { ...(prev[projectKey]?.[personKey] ?? {}) };
      if (rounded > 0) nextPersonActuals[mk] = rounded;
      else delete nextPersonActuals[mk];
      return {
        ...prev,
        [projectKey]: {
          ...(prev[projectKey] ?? {}),
          [personKey]: nextPersonActuals,
        },
      };
    });
    try {
      if (rounded === 0) await deleteActualCell(projId, personId, mk);
      else await putActualCell(projId, personId, mk, rounded);
    } catch {
      const res = await apiFetch(`/api/projects/${projId}/actuals`);
      if (res.ok) {
        const body = (await res.json()) as Record<string, Record<string, number>>;
        setActuals((prev) => ({ ...prev, [projectKey]: body }));
      }
    } finally {
      setPendingActualCells((prev) => {
        const next = new Set(prev);
        next.delete(cellKey);
        return next;
      });
    }
  }

  async function updateAlloc(projId: number, mid: number, mk: YmKey, val: AllocationCell | 0) {
    // Optimistic apply, then PUT/DELETE; refetch on error so the user sees
    // authoritative state instead of a blind revert.
    const projectRow = projects.find((p) => p.id === projId);
    const memberRow = projectRow?.members.find((m: Member) => m.id === mid);
    const personRef = memberRow?.pid ? getPerson(memberRow.pid, people) : null;
    const optimisticPct = val === 0 ? 0 : cellInputToBarePercent(val, personRef, mk);
    setProjects(
      projects.map((p: Project) => {
        if (p.id !== projId) return p;
        return {
          ...p,
          members: p.members.map((m: Member) => {
            if (m.id !== mid) return m;
            const na: Record<YmKey, number> = { ...m.a };
            if (optimisticPct > 0) na[mk] = optimisticPct;
            else delete na[mk];
            return { ...m, a: na };
          }),
        };
      }),
    );
    try {
      if (val === 0) {
        await deleteAllocationCell(projId, mid, mk);
      } else {
        await putAllocationCell(projId, mid, mk, val);
      }
    } catch {
      await refetchProject(projId);
    }
  }
  async function assignPerson(projId: number, mid: number, person: Person) {
    try {
      const updated = await patchProjectMember(projId, mid, { pid: person.id });
      setProjects(
        projects.map((p: Project) =>
          p.id !== projId
            ? p
            : { ...p, members: p.members.map((m: Member) => (m.id === mid ? updated : m)) },
        ),
      );
    } catch {
      // toast surfaced
    }
  }
  async function setMemberRole(projId: number, mid: number, r: string) {
    try {
      const updated = await patchProjectMember(projId, mid, { role: r || "" });
      setProjects(
        projects.map((p: Project) =>
          p.id !== projId
            ? p
            : { ...p, members: p.members.map((m: Member) => (m.id === mid ? updated : m)) },
        ),
      );
    } catch {
      // toast surfaced
    }
  }
  /* Add a blank member row to a project. Server assigns the id (autoincrement
     integer) and persists the row inside a transaction; the SPA appends the
     returned row to local state. */
  async function addTeamMember(projId: number) {
    try {
      const created = await addProjectMember(projId);
      setProjects(
        projects.map((p: Project) =>
          p.id !== projId ? p : { ...p, members: [...p.members, created] },
        ),
      );
    } catch {
      // memberships-api already surfaced the error via toast.
    }
  }
  async function removeRow(projId: number, mid: number) {
    try {
      await deleteProjectMember(projId, mid);
      setProjects(
        projects.map((p: Project) =>
          p.id !== projId ? p : { ...p, members: p.members.filter((m: Member) => m.id !== mid) },
        ),
      );
    } catch {
      // memberships-api already surfaced the error via toast.
    }
  }

  function exportProject(project: Project) {
    const rows: (string | number | null)[][] = [
      ["Project", project.name, project.code, project.status],
    ];
    rows.push(["Member", "Role", "Pid", ...months.map((m) => m.label)]);
    for (const m of project.members) {
      const person = m.pid ? getPerson(m.pid, people) : null;
      const row: (string | number | null)[] = [
        person?.name || "(Unassigned)",
        memberRole(project, m),
        m.pid ?? "",
      ];
      for (const ym of months) {
        if (isActualMode) {
          row.push(m.pid ? getActualHours(actuals, project.id, m.pid, ym.key) || "" : "");
        } else {
          const a = m.a[ym.key];
          if (a == null) row.push("");
          else if (viewMode === "hours")
            row.push(
              `${Math.round(allocToHours(a, person ?? undefined, ym.key, extrasForYmKey(ym.key)))}h`,
            );
          else row.push(`${a}%`);
        }
      }
      rows.push(row);
    }
    downloadCsv(`dolmit-${project.code.toLowerCase()}-${mode}.csv`, rows);
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1600, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            Projects view
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            Click any cell to edit · Enter to confirm · Esc to cancel
            {lockPast &&
              !isActualMode &&
              ` · editable from ${prevMonthLabel(PREV_MONTH_KEY)} onwards`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <MultiSelect
            options={sortByName(
              projects.map((p) => ({
                id: p.id,
                name: p.name,
                code: p.code,
                status: p.status,
              })),
            )}
            selected={selectedIds}
            onChange={setSelectedIds}
            placeholder="Select projects…"
          />
          <Seg
            options={[
              ["pct", "%"],
              ["hours", "Hours"],
            ]}
            value={viewMode}
            onChange={(v) => setViewMode(v as "pct" | "hours")}
          />
        </div>
      </div>

      {selProjects.length === 0 && (
        <Card>
          <Empty
            icon={
              <svg aria-hidden="true" width="40" height="40" viewBox="0 0 40 40" fill="none">
                <rect
                  x="6"
                  y="10"
                  width="28"
                  height="22"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M6 16h28M12 20h8M12 24h12M12 28h6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            }
            title="No projects selected"
            msg="Use the selector above to pick one or more projects and view their allocation grids."
          />
        </Card>
      )}

      {syncToast && (
        <output
          style={{
            background: syncToast.startsWith("Sync failed") ? C.rBg : C.gBg,
            border: `1px solid ${syncToast.startsWith("Sync failed") ? C.rBd : C.gBd}`,
            color: syncToast.startsWith("Sync failed") ? C.r : C.g,
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 12,
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ flex: 1 }}>{syncToast}</span>
          <button
            type="button"
            onClick={() => setSyncToast(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "inherit",
              fontSize: 14,
              lineHeight: 1,
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </output>
      )}

      {syncConfirm && (
        <Modal
          title="Sync from Jira"
          onClose={() => {
            setSyncConfirm(null);
            setSyncConfirmCode("");
          }}
        >
          <div style={{ display: "grid", gap: 16, minWidth: 360 }}>
            <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.5 }}>
              Pull Tempo worklog totals for <strong>{syncConfirm.name}</strong>.
            </div>
            <div style={{ fontSize: 12, color: C.slateMut, lineHeight: 1.5 }}>
              Tempo-reported (person × month) cells are added or updated. Cells Tempo doesn't report
              are left unchanged — including any you've edited manually. Nothing is deleted or
              replaced.
            </div>
            <label style={{ display: "grid", gap: 4, fontSize: 12, color: C.slate }}>
              <span>
                To enable full-history sync, type project code <code>{syncConfirm.code}</code>{" "}
                exactly.
              </span>
              <input
                value={syncConfirmCode}
                onChange={(e) => setSyncConfirmCode(e.currentTarget.value)}
                placeholder={syncConfirm.code}
                style={{
                  border: `1px solid ${C.line}`,
                  borderRadius: 6,
                  padding: "7px 9px",
                  font: "inherit",
                }}
              />
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Btn
                ghost
                onClick={() => {
                  setSyncConfirm(null);
                  setSyncConfirmCode("");
                }}
              >
                Cancel
              </Btn>
              <Btn
                onClick={() => {
                  const p = syncConfirm;
                  setSyncConfirm(null);
                  setSyncConfirmCode("");
                  void runJiraSync(p, "recent");
                }}
                title="Pull worklogs from the last 2 months"
              >
                Sync recent (2 mo)
              </Btn>
              <Btn
                primary
                onClick={() => {
                  const p = syncConfirm;
                  const code = syncConfirmCode;
                  setSyncConfirm(null);
                  setSyncConfirmCode("");
                  void runJiraSync(p, "all", code);
                }}
                disabled={syncConfirmCode !== syncConfirm.code}
                title="Pull worklogs from up to 5 years back — full history"
              >
                Sync ALL
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {selProjects.map((project) => {
        const totalAssigned = project.members.filter((m) => m.pid).length;
        const unassigned = project.members.filter((m) => !m.pid).length;
        return (
          <div key={project.id} style={{ marginBottom: 22 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: C.navyDeep,
                  fontFamily: "'Poppins',system-ui,sans-serif",
                }}
              >
                {project.name}
              </span>
              <span
                style={{
                  fontSize: 11.5,
                  color: C.slateMut,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                }}
              >
                {project.code}
              </span>
              <SBadge s={project.status} />
              <span style={{ fontSize: 11.5, color: C.slateMut, marginLeft: 4 }}>
                <strong style={{ color: C.navy, fontWeight: 600 }}>{totalAssigned}</strong> assigned
                {unassigned > 0 && (
                  <span style={{ color: C.r, marginLeft: 8, fontWeight: 600 }}>
                    · {unassigned} unassigned
                  </span>
                )}
              </span>
              {(role === "admin" || role === "lead" || role === "accountant") && (
                <span style={{ fontSize: 11.5, color: C.slateMut, marginLeft: 4 }}>
                  €{project.budget.toLocaleString()} · {project.mt}% margin · {(() => {
                    if (project.pm !== "flat") return "Role-based";
                    const { fr } = getProjectRates(project, CURRENT_MONTH_KEY);
                    const indexed =
                      Array.isArray(project.rateHistory) && project.rateHistory.length > 1;
                    return `Flat €${fr}/h${indexed ? " · indexed" : ""}`;
                  })()}
                </span>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <Btn
                  small
                  onClick={() => exportProject(project)}
                  icon={
                    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 14 14">
                      <path
                        d="M7 1v8m0 0l-3-3m3 3l3-3M2 12h10"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        fill="none"
                      />
                    </svg>
                  }
                >
                  CSV
                </Btn>
                <Btn
                  small
                  onClick={() => addTeamMember(project.id)}
                  icon={
                    <svg aria-hidden="true" width="11" height="11" viewBox="0 0 12 12">
                      <path
                        d="M6 2v8M2 6h8"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  }
                >
                  Add Team Member
                </Btn>
                {role === "admin" && (
                  <Btn small onClick={() => onEdit?.(project)}>
                    Edit Project
                  </Btn>
                )}
                {canSyncJira && !project.isTimeOff && project.status !== "archived" && (
                  <Btn
                    small
                    onClick={() => setSyncConfirm(project)}
                    disabled={syncBusyForProject === project.id}
                    title="Replace all actuals on this project with Tempo's worklog history"
                  >
                    {syncBusyForProject === project.id ? "Syncing…" : "Sync from Jira"}
                  </Btn>
                )}
              </div>
            </div>
            <Card style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        ...TH,
                        textAlign: "left",
                        minWidth: 180,
                        position: "sticky",
                        left: 0,
                        top: 0,
                        zIndex: 5,
                        background: C.canvas,
                      }}
                    >
                      Team Member
                    </th>
                    <th
                      style={{
                        ...TH,
                        textAlign: "center",
                        minWidth: 80,
                        position: "sticky",
                        left: 180,
                        top: 0,
                        zIndex: 5,
                        background: C.canvas,
                        borderRight: `1px solid ${C.line}`,
                      }}
                    >
                      Role
                    </th>
                    {months.map((m) => {
                      const isCurrent = m.isCurrent;
                      return (
                        <th
                          key={m.key}
                          data-current-month={isCurrent ? "true" : undefined}
                          style={{
                            ...TH,
                            textAlign: "center",
                            minWidth: cellMin,
                            position: "sticky",
                            top: 0,
                            zIndex: 3,
                            background: isCurrent ? C.blueTint2 : C.canvas,
                            borderBottom: `1px solid ${C.line}`,
                            boxShadow: isCurrent ? `inset 0 -2px 0 ${C.blue}` : undefined,
                          }}
                        >
                          <MonthHeader m={m} isCurrent={isCurrent} />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortMembers(project.members)
                    .filter((m: Member) => {
                      // Hide archived people with no data in the visible period.
                      // Active people and unassigned slots always show.
                      if (!m.pid) return true;
                      const person = getPerson(m.pid, people);
                      if (person?.excludeFromPlanning) return false;
                      if (!person || person.status !== "archived") return true;
                      for (const ym of months) {
                        const a = m.a[ym.key];
                        if (typeof a === "number" && a !== 0) return true;
                        if (getActualHours(actuals, project.id, m.pid, ym.key)) return true;
                      }
                      return false;
                    })
                    .map((m: Member, ri: number) => {
                      const person = m.pid ? getPerson(m.pid, people) : null;
                      const rowBg = ri % 2 ? C.surfaceAlt : C.surface;
                      const declaredRoles = Object.keys(project.roles);
                      const archived = person?.status === "archived";
                      return (
                        <tr
                          key={m.id}
                          data-testid={`member-row-${m.id}`}
                          style={archived ? { opacity: 0.55 } : undefined}
                        >
                          <td
                            style={{
                              ...TD,
                              textAlign: "left",
                              position: "sticky",
                              left: 0,
                              background: rowBg,
                              zIndex: 2,
                            }}
                          >
                            {person ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => openPerson?.(person.id)}
                                  title="Open in Person view"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                    textAlign: "left",
                                  }}
                                >
                                  <Av name={person.name} sz={24} />
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: C.navyDeep,
                                      fontSize: 12.5,
                                      textDecoration: "underline",
                                      textDecorationColor: "transparent",
                                      textUnderlineOffset: 2,
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.textDecorationColor = C.blue;
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.textDecorationColor = "transparent";
                                    }}
                                  >
                                    {person.name}
                                  </span>
                                </button>
                                {/* Per-membership cost pencil removed in v0.2.0 vol2.
                                    Per-project costs now live on the person side —
                                    edit them in People setup → Edit person → "Cost per project". */}
                              </div>
                            ) : (
                              <AssignPicker
                                people={people}
                                excludePids={[]}
                                onPick={(p) => assignPerson(project.id, m.id, p)}
                              />
                            )}
                          </td>
                          <td
                            style={{
                              ...TD,
                              textAlign: "left",
                              position: "sticky",
                              left: 180,
                              background: rowBg,
                              zIndex: 2,
                              borderRight: `1px solid ${C.line}`,
                              padding: "6px 8px",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <RolePicker
                                value={m.role}
                                onChange={(r) => setMemberRole(project.id, m.id, r)}
                                declared={declaredRoles}
                                catalogue={JOB_ROLES_SEED as unknown as string[]}
                              />
                              <button
                                type="button"
                                onClick={() => removeRow(project.id, m.id)}
                                title="Remove this row"
                                style={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 4,
                                  background: "transparent",
                                  border: `1px solid ${C.line}`,
                                  color: C.slateMut,
                                  fontSize: 11,
                                  cursor: "pointer",
                                  padding: 0,
                                  lineHeight: 1,
                                  flexShrink: 0,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = C.rBg;
                                  e.currentTarget.style.color = C.r;
                                  e.currentTarget.style.borderColor = C.rBd;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                  e.currentTarget.style.color = C.slateMut;
                                  e.currentTarget.style.borderColor = C.line;
                                }}
                              >
                                ×
                              </button>
                            </div>
                          </td>
                          {months.map((ym) => {
                            const raw = m.a[ym.key];
                            /* Cells in a time-off project render in warm sand so the PM can
                     see time-off at a glance in the grid. */
                            const isTimeOff = !!project.isTimeOff;
                            if (isActualMode) {
                              const h = m.pid
                                ? getActualHours(actuals, project.id, m.pid, ym.key)
                                : 0;
                              const capacityHours =
                                ((person?.wl ?? 100) / 100) *
                                whForMonth(ym.key, extrasForYmKey(ym.key));
                              const pct = person ? Math.round((h / capacityHours) * 100) : 0;
                              return (
                                <ActualCell
                                  key={ym.key}
                                  hours={h}
                                  pct={pct}
                                  viewMode={viewMode}
                                  capacityHours={capacityHours}
                                  isCurrent={ym.isCurrent}
                                  disabled={pendingActualCells.has(
                                    `${project.id}:${m.pid ?? 0}:${ym.key}`,
                                  )}
                                  onChange={(hours) => {
                                    if (m.pid) void updateActual(project.id, m.pid, ym.key, hours);
                                  }}
                                  testId={`actual-cell-${m.id}-${ym.key}`}
                                />
                              );
                            }
                            return (
                              <AllocCell
                                key={ym.key}
                                val={raw}
                                isPlanned={project.status === "planned"}
                                isTimeOff={isTimeOff}
                                isPast={editBeforeKey ? ym.key < editBeforeKey : ym.isPast}
                                lockPast={lockPast}
                                onChange={(v) => updateAlloc(project.id, m.id, ym.key, v)}
                                viewMode={viewMode}
                                personWl={person?.wl || 100}
                                ymKey={ym.key}
                                testId={`alloc-cell-${m.id}-${ym.key}`}
                              />
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </Card>
          </div>
        );
      })}

      {/* Legend */}
      {selProjects.length > 0 && (
        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 18,
            fontSize: 11,
            color: C.slateMut,
            flexWrap: "wrap",
          }}
        >
          {(
            [
              ["Active ≥80%", C.blueTint2, C.blueTint4, C.blue],
              ["Over-planned", C.rBg, C.rBd, C.r],
              ["Planned status", C.blueTint2, C.blueTint4, C.blueDeep],
              ["Time off", C.vacBg, C.vacBd, C.vac],
              ["Past month (locked)", C.lock, C.line, C.slateMut],
            ] as Array<[string, string, string, string]>
          ).map(([l, bg, bd, co]) => (
            <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 3,
                  background: bg,
                  border: `1px solid ${bd}`,
                }}
              />
              <span style={{ color: co, fontWeight: 600 }}>{l}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export interface PersonDetailViewProps {
  projects: Project[];
  people: Person[];
  setProjects: (next: Project[]) => void;
  rangeStart: string;
  rangeEnd: string;
  mode: SpaMode;
  actuals: ActualsByProject;
  selectedPersonId: number | null;
  setSelectedPersonId: (pid: number | null) => void;
  role: string;
  openProject?: (projectId: number) => void;
}

export const PersonDetailView: FC<PersonDetailViewProps> = ({
  projects,
  people,
  setProjects,
  rangeStart,
  rangeEnd,
  mode,
  actuals,
  role,
  selectedPersonId,
  setSelectedPersonId,
  openProject,
}) => {
  const { C } = useC();
  const { TH, TD } = useTableStyles();
  const plannablePeople = people.filter((p) => !p.excludeFromPlanning);
  const requested = selectedPersonId ?? plannablePeople[0]?.id ?? people[0]?.id ?? 1;
  const requestedPerson = getPerson(requested, people);
  const sel = requestedPerson?.excludeFromPlanning
    ? (plannablePeople[0]?.id ?? requested)
    : requested;
  const setSel = (id: number) => setSelectedPersonId(id);
  const person = getPerson(sel, people);
  const months = windowMonths(rangeStart, rangeEnd);
  const totals = aggregatePersonAllocations(sel, projects, months);
  useScrollCurrentIntoView(rangeStart, rangeEnd, `person:${sel}`);
  const [viewMode, setViewMode] = usePersistedState<"pct" | "hours">(
    "person",
    "displayUnit",
    "pct",
  );
  const isActualMode = mode === "actuals";
  const zoom = 1;
  const cellMin = Math.round(58 * zoom);
  const editBeforeKey = role === "admin" || role === "lead" ? null : PREV_MONTH_KEY;
  const lockPast = role !== "admin" && role !== "lead";

  function cellInputToBarePercent(
    val: AllocationCell,
    p: Pick<Person, "wl"> | null | undefined,
    ymKey: YmKey,
  ): number {
    if (val.u === "percent") return val.v;
    return hoursToPercent(val.v, p?.wl ?? 100, whForMonth(ymKey, extrasForYmKey(ymKey)));
  }

  async function refetchProject(projId: number): Promise<void> {
    try {
      const res = await apiFetch(`/api/projects/${projId}`);
      if (!res.ok) return;
      const refreshed = (await res.json()) as Project;
      setProjects(projects.map((p: Project) => (p.id === projId ? refreshed : p)));
    } catch {
      // best-effort — apiFetch already handles single-flight token refresh
    }
  }

  async function updateAlloc(projId: number, mid: number, mk: YmKey, val: AllocationCell | 0) {
    const projectRow = projects.find((p) => p.id === projId);
    const memberRow = projectRow?.members.find((m: Member) => m.id === mid);
    const personRef = memberRow?.pid ? getPerson(memberRow.pid, people) : null;
    const optimisticPct = val === 0 ? 0 : cellInputToBarePercent(val, personRef, mk);
    setProjects(
      projects.map((p: Project) => {
        if (p.id !== projId) return p;
        return {
          ...p,
          members: p.members.map((m: Member) => {
            if (m.id !== mid) return m;
            const na: Record<YmKey, number> = { ...m.a };
            if (optimisticPct > 0) na[mk] = optimisticPct;
            else delete na[mk];
            return { ...m, a: na };
          }),
        };
      }),
    );
    try {
      if (val === 0) {
        await deleteAllocationCell(projId, mid, mk);
      } else {
        await putAllocationCell(projId, mid, mk, val);
      }
    } catch {
      await refetchProject(projId);
    }
  }

  const projsForPerson = projects.filter((p: Project) =>
    p.members.some((m: Member) => m.pid === sel),
  );

  /* Add this person to a project they aren't on yet via the server. */
  async function addToProject(projId: number) {
    try {
      const created = await addPersonMembership(sel, projId);
      setProjects(
        projects.map((p: Project) =>
          p.id !== projId ? p : { ...p, members: [...p.members, created] },
        ),
      );
    } catch {
      // toast surfaced
    }
  }
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const availableProjects = projects.filter(
    (p) => p.status !== "archived" && !p.members.some((m) => m.pid === sel),
  );

  function exportTimeline() {
    const rows: (string | number | null | undefined)[][] = [
      ["Person", person?.name, person?.email],
    ];
    rows.push(["Project", "Role", ...months.map((m) => m.label)]);
    for (const proj of projsForPerson) {
      for (const m of proj.members.filter((mm) => mm.pid === sel)) {
        const row: (string | number | null)[] = [proj.name, memberRole(proj, m)];
        for (const ym of months) {
          if (isActualMode) {
            row.push(getActualHours(actuals, proj.id, sel, ym.key) || "");
          } else {
            const a = m.a[ym.key];
            if (a == null) row.push("");
            else if (viewMode === "hours")
              row.push(`${Math.round(allocToHours(a, person, ym.key, extrasForYmKey(ym.key)))}h`);
            else row.push(`${a}%`);
          }
        }
        rows.push(row);
      }
    }
    rows.push([
      "TOTAL",
      "",
      ...months.map((ym) => {
        if (isActualMode) {
          let h = 0;
          for (const p of projsForPerson) {
            h += getActualHours(actuals, p.id, sel, ym.key) || 0;
          }
          return h || "";
        }
        const t = totals[ym.key] ?? 0;
        if (t === 0) return "";
        if (viewMode === "hours") {
          const availH = ((person?.wl || 100) / 100) * whForMonth(ym.key, extrasForYmKey(ym.key));
          return `${Math.round((t / 100) * availH)}h`;
        }
        return `${Math.round(t)}%`;
      }),
    ]);
    downloadCsv(
      `dolmit-${(person?.name || "person").toLowerCase().replace(/\s+/g, "-")}-${mode}.csv`,
      rows,
    );
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1600, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: C.navyDeep,
              margin: 0,
              fontFamily: "'Poppins',system-ui,sans-serif",
              letterSpacing: -0.3,
            }}
          >
            Person Timeline
          </h2>
          <p style={{ color: C.slateMut, fontSize: 12.5, margin: "3px 0 0" }}>
            Allocations across all projects for one person
          </p>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <select
            value={sel}
            onChange={(e) => setSel(Number(e.target.value))}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: `1px solid ${C.line}`,
              fontSize: 12.5,
              color: C.navy,
              background: C.surface,
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              fontWeight: 600,
            }}
          >
            {sortByName(plannablePeople).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {person && (
            <span style={{ fontSize: 11.5, color: C.slateMut }}>
              Capacity <strong style={{ color: C.navy }}>{person.wl}%</strong> ·{" "}
              {person.emp === "full" ? "Full-time" : "Part-time"}
            </span>
          )}
          <Seg
            options={[
              ["pct", "%"],
              ["hours", "Hours"],
            ]}
            value={viewMode}
            onChange={(v) => setViewMode(v as "pct" | "hours")}
          />
          <div style={{ position: "relative" }}>
            <Btn
              small
              onClick={() => setAddMenuOpen((o) => !o)}
              disabled={availableProjects.length === 0 || !!person?.excludeFromPlanning}
              icon={
                <svg aria-hidden="true" width="11" height="11" viewBox="0 0 12 12">
                  <path
                    d="M6 2v8M2 6h8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              }
              title={
                person?.excludeFromPlanning
                  ? "Person is excluded from planning"
                  : availableProjects.length === 0
                    ? "Already on every project"
                    : "Add this person to another project"
              }
            >
              Add to project
            </Btn>
            {addMenuOpen && availableProjects.length > 0 && (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setAddMenuOpen(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 40,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "default",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    right: 0,
                    zIndex: 41,
                    background: C.surface,
                    border: `1px solid ${C.line}`,
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(15,23,42,.12)",
                    minWidth: 260,
                    maxHeight: 320,
                    overflow: "auto",
                    padding: "4px 0",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      color: C.slateMut,
                      padding: "6px 12px 4px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.3,
                    }}
                  >
                    Pick a project
                  </div>
                  {availableProjects.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => {
                        void addToProject(p.id);
                        setAddMenuOpen(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        width: "100%",
                        padding: "8px 12px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        fontSize: 12.5,
                        color: C.navy,
                        fontFamily: "inherit",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = C.surfaceAlt;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <span style={{ flex: 1, fontWeight: 600 }}>{p.name}</span>
                      <span
                        style={{
                          fontSize: 10.5,
                          color: C.slateMut,
                          fontFamily: "'Source Sans Pro',monospace",
                        }}
                      >
                        {p.code}
                      </span>
                      <SBadge s={p.status} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <Btn
            small
            onClick={exportTimeline}
            icon={
              <svg aria-hidden="true" width="11" height="11" viewBox="0 0 14 14">
                <path
                  d="M7 1v8m0 0l-3-3m3 3l3-3M2 12h10"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            }
          >
            CSV
          </Btn>
        </div>
      </div>

      <Card style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)" }}>
        {projsForPerson.length === 0 ? (
          <Empty
            title="No allocations yet"
            msg={`${person?.name || "This person"} is not assigned to any project.`}
          />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    ...TH,
                    textAlign: "left",
                    minWidth: 200,
                    position: "sticky",
                    left: 0,
                    top: 0,
                    zIndex: 5,
                    background: C.canvas,
                  }}
                >
                  Project
                </th>
                <th
                  style={{
                    ...TH,
                    textAlign: "center",
                    minWidth: 70,
                    position: "sticky",
                    left: 200,
                    top: 0,
                    zIndex: 5,
                    background: C.canvas,
                    borderRight: `1px solid ${C.line}`,
                  }}
                >
                  Role
                </th>
                {months.map((m) => {
                  const isCurrent = m.isCurrent;
                  return (
                    <th
                      key={m.key}
                      data-current-month={isCurrent ? "true" : undefined}
                      style={{
                        ...TH,
                        textAlign: "center",
                        minWidth: cellMin,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                        background: isCurrent ? C.blueTint2 : C.canvas,
                        borderBottom: `1px solid ${C.line}`,
                        boxShadow: isCurrent ? `inset 0 -2px 0 ${C.blue}` : undefined,
                      }}
                    >
                      <MonthHeader m={m} isCurrent={isCurrent} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {projects.map((proj) => {
                const ms = proj.members.filter((m) => m.pid === sel);
                if (!ms.length) return null;
                return ms.map((m, mi) => {
                  const rowBg = mi % 2 ? C.surfaceAlt : C.surface;
                  return (
                    <tr key={`${proj.id}-${m.id || mi}`}>
                      <td
                        style={{
                          ...TD,
                          textAlign: "left",
                          position: "sticky",
                          left: 0,
                          background: rowBg,
                          zIndex: 2,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button
                            type="button"
                            onClick={() => openProject?.(proj.id)}
                            title="Open in Projects view"
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              margin: 0,
                              cursor: openProject ? "pointer" : "default",
                              font: "inherit",
                              fontWeight: 600,
                              color: C.navyDeep,
                              fontSize: 12.5,
                              textAlign: "left",
                              textDecoration: "underline",
                              textDecorationColor: "transparent",
                              textUnderlineOffset: 2,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecorationColor = C.blue;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecorationColor = "transparent";
                            }}
                          >
                            {proj.name}
                          </button>
                          <SBadge s={proj.status} />
                        </div>
                      </td>
                      <td
                        style={{
                          ...TD,
                          textAlign: "center",
                          position: "sticky",
                          left: 200,
                          background: rowBg,
                          zIndex: 2,
                          borderRight: `1px solid ${C.line}`,
                        }}
                      >
                        <Badge tone={roleTone(memberRole(proj, m) || "")}>
                          {memberRole(proj, m)}
                        </Badge>
                      </td>
                      {months.map((ym) => {
                        const raw = m.a[ym.key];
                        const isTimeOff = !!proj.isTimeOff;
                        if (isActualMode) {
                          const h = getActualHours(actuals, proj.id, sel, ym.key);
                          const pct = person
                            ? Math.round(
                                (h /
                                  (((person.wl || 100) / 100) *
                                    whForMonth(ym.key, extrasForYmKey(ym.key)))) *
                                  100,
                              )
                            : 0;
                          const label =
                            h > 0 ? (viewMode === "hours" ? `${Math.round(h)}h` : `${pct}%`) : "·";
                          const bg = h > 0 ? C.blueTint1 : "transparent";
                          const co = h > 0 ? C.navy : C.slateDim;
                          return (
                            <td
                              key={ym.key}
                              style={{
                                ...TD,
                                textAlign: "center",
                                background: bg,
                                minWidth: cellMin,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              <span
                                style={{ fontWeight: h > 0 ? 600 : 400, color: co, fontSize: 12.5 }}
                              >
                                {label}
                              </span>
                            </td>
                          );
                        }
                        return (
                          <AllocCell
                            key={ym.key}
                            val={raw}
                            isPlanned={proj.status === "planned"}
                            isTimeOff={isTimeOff}
                            isPast={editBeforeKey ? ym.key < editBeforeKey : ym.isPast}
                            lockPast={lockPast}
                            onChange={(v) => updateAlloc(proj.id, m.id, ym.key, v)}
                            viewMode={viewMode}
                            personWl={person?.wl || 100}
                            ymKey={ym.key}
                            testId={`alloc-cell-${m.id}-${ym.key}`}
                          />
                        );
                      })}
                    </tr>
                  );
                });
              })}
              <tr style={{ background: C.blueTint1 }}>
                <td
                  colSpan={2}
                  style={{
                    ...TD,
                    textAlign: "left",
                    fontWeight: 700,
                    color: C.navyDeep,
                    fontSize: 12.5,
                    borderTop: `2px solid ${C.blueTint4}`,
                    position: "sticky",
                    left: 0,
                    background: C.blueTint1,
                    zIndex: 2,
                  }}
                >
                  Total
                </td>
                {months.map((ym) => {
                  let displayT = "·";
                  let bg = "transparent";
                  let co = C.slateDim;
                  // Cells are wl-normalised per ADR-0001, so the threshold
                  // for "over capacity" is 100 regardless of the person's wl.
                  const FTE = 100;
                  const tol = 5;
                  if (isActualMode) {
                    let h = 0;
                    for (const p of projsForPerson)
                      h += getActualHours(actuals, p.id, sel, ym.key) || 0;
                    const pct = person
                      ? Math.round(
                          (h /
                            (((person.wl || 100) / 100) *
                              whForMonth(ym.key, extrasForYmKey(ym.key)))) *
                            100,
                        )
                      : 0;
                    if (h > 0) {
                      displayT = viewMode === "hours" ? `${Math.round(h)}h` : `${pct}%`;
                      if (pct > FTE + tol) {
                        bg = C.rBg;
                        co = C.r;
                      } else if (pct >= FTE - tol) {
                        bg = C.gBg;
                        co = C.g;
                      } else {
                        bg = C.yBg;
                        co = C.y;
                      }
                    }
                  } else {
                    const t = totals[ym.key] || 0;
                    const availH =
                      ((person?.wl || 100) / 100) * whForMonth(ym.key, extrasForYmKey(ym.key));
                    const totalHours = Math.round((t / 100) * availH);
                    if (t > 0) {
                      if (t > FTE + tol) {
                        bg = C.rBg;
                        co = C.r;
                      } else if (t >= FTE - tol) {
                        bg = C.gBg;
                        co = C.g;
                      } else {
                        bg = C.yBg;
                        co = C.y;
                      }
                    }
                    displayT =
                      t > 0 ? (viewMode === "hours" ? `${totalHours}h` : `${Math.round(t)}%`) : "·";
                  }
                  return (
                    <td
                      key={ym.key}
                      style={{
                        ...TD,
                        textAlign: "center",
                        background: bg,
                        borderTop: `2px solid ${C.blueTint4}`,
                        minWidth: cellMin,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          color: co,
                          fontSize: 12.5,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {displayT}
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        )}
      </Card>
      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 18,
          fontSize: 11,
          color: C.slateMut,
          flexWrap: "wrap",
        }}
      >
        {(
          [
            ["Within capacity (±5%)", C.gBg, C.gBd],
            ["Under-planned", C.yBg, C.yBd],
            ["Over-planned", C.rBg, C.rBd],
          ] as Array<[string, string, string]>
        ).map(([l, bg, bd]) => (
          <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 11,
                height: 11,
                borderRadius: 3,
                background: bg,
                border: `1px solid ${bd}`,
              }}
            />
            {l}
          </span>
        ))}
      </div>
    </div>
  );
};
